import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api, errMsg } from "../lib/api";

const TEASERS = [
  "Hey! Welcome to Jack Cash. Need a hand getting started?",
  "Send and receive money instantly with your own Cash Tag.",
  "Got a question? Tap here and ask me anything, 24/7.",
];

// Stable across remounts so teaser timing is deterministic.
let teaserStartTs = null;

const Avatar = () => (
  <div className="w-8 h-8 shrink-0 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
    <Sparkles size={16} />
  </div>
);

export default function PublicChat() {
  const [open, setOpen] = useState(false);
  const [teasers, setTeasers] = useState([]);
  const [dismissedTeasers, setDismissedTeasers] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [convId, setConvId] = useState(null);
  const scrollRef = useRef(null);

  // Poll the public support conversation for agent replies once escalated.
  useEffect(() => {
    if (!convId || !sessionId) return;
    let stop = false;
    const seen = new Set();
    const tick = async () => {
      try {
        const { data } = await api.get(`/support/public/conversation/${convId}?session_id=${encodeURIComponent(sessionId)}`);
        const fresh = data.messages.filter((m) => (m.sender === "agent" || m.sender === "system") && !seen.has(m.msg_id));
        if (fresh.length) {
          fresh.forEach((m) => seen.add(m.msg_id));
          setMessages((cur) => [...cur, ...fresh.map((m) => ({ role: m.sender === "agent" ? "agent" : "system", content: m.body }))]);
        }
      } catch {}
    };
    tick();
    const id = setInterval(() => !stop && tick(), 5000);
    return () => { stop = true; clearInterval(id); };
  }, [convId, sessionId]);

  const escalate = async () => {
    if (convId) return;
    const sid = sessionId || `web_${Date.now()}`;
    setSessionId(sid);
    const lastUser = [...messages].reverse().find((m) => m.role === "user")?.content || "I need to talk to a person";
    try {
      const { data } = await api.post("/support/public/escalate", { session_id: sid, category: "general", message: lastUser });
      setConvId(data.conv_id);
      setMessages((m) => [...m, { role: "system", content: data.message }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: errMsg(err) }]);
    }
  };

  // Reveal one teaser every 5 seconds based on wall-clock time (resilient to remounts).
  useEffect(() => {
    if (open || dismissedTeasers) return;
    if (!teaserStartTs) teaserStartTs = Date.now();
    const tick = () => {
      const count = Math.min(3, Math.floor((Date.now() - teaserStartTs) / 5000));
      setTeasers(TEASERS.slice(0, count));
      return count;
    };
    tick();
    const id = setInterval(() => {
      if (tick() >= 3) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [open, dismissedTeasers]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const openChat = () => {
    setOpen(true);
    setTeasers([]);
    setDismissedTeasers(true);
    if (messages.length === 0) {
      setMessages([{ role: "assistant", content: "Hi! I'm the Jack Cash assistant. How can I help you today?" }]);
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    if (convId) {
      try { await api.post(`/support/public/conversation/${convId}/message`, { session_id: sessionId, message: text }); }
      catch (err) { setMessages((m) => [...m, { role: "assistant", content: errMsg(err) }]); }
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/public-chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: errMsg(err) }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col items-end gap-3" data-testid="public-chat-root">
      {/* Teaser pop-ups */}
      <AnimatePresence>
        {!open &&
          teasers.map((t, i) => (
            <motion.button
              key={i}
              initial={{ opacity: 0, y: 12, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              onClick={openChat}
              data-testid={`public-chat-teaser-${i}`}
              className="max-w-[260px] text-left bg-card border border-border shadow-xl rounded-2xl rounded-br-sm px-4 py-3 flex items-start gap-2.5 hover:border-primary/50 transition-colors"
            >
              <Avatar />
              <span className="text-sm text-foreground leading-snug">{t}</span>
            </motion.button>
          ))}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            data-testid="public-chat-panel"
            className="w-[90vw] max-w-[380px] h-[520px] max-h-[75vh] bg-card border border-border rounded-3xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3.5 bg-primary text-primary-foreground">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                  <Sparkles size={18} />
                </div>
                <div>
                  <p className="font-semibold text-sm leading-tight">Jack Cash Assistant</p>
                  <p className="text-[11px] text-primary-foreground/80 leading-tight">Online now</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="public-chat-close" className="p-1.5 rounded-full hover:bg-white/15 transition-colors">
                <X size={18} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-background" data-testid="public-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && <Avatar />}
                  {m.role === "system" ? (
                    <div className="mx-auto text-[11px] text-muted-foreground bg-secondary/60 px-3 py-1.5 rounded-full">{m.content}</div>
                  ) : (
                    <div
                      className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm leading-snug ${
                        m.role === "user"
                          ? "bg-primary text-primary-foreground rounded-br-sm"
                          : m.role === "agent"
                          ? "bg-success/15 border border-success/30 text-foreground rounded-bl-sm"
                          : "bg-secondary text-foreground rounded-bl-sm"
                      }`}
                    >
                      {m.role === "agent" && <div className="text-[10px] font-bold uppercase text-success mb-0.5">Support agent</div>}
                      {m.content}
                    </div>
                  )}
                </div>
              ))}
              {busy && (
                <div className="flex items-end gap-2 justify-start">
                  <Avatar />
                  <div className="bg-secondary px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1">
                    {[0, 1, 2].map((d) => (
                      <motion.span key={d} className="w-1.5 h-1.5 rounded-full bg-muted-foreground"
                        animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={send} className="p-3 border-t border-border bg-card flex flex-col gap-2">
              {!convId && (
                <button type="button" data-testid="public-talk-to-human" onClick={escalate}
                  className="self-start text-[11px] font-semibold text-primary">Talk to a human →</button>
              )}
              <div className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your message..."
                  data-testid="public-chat-input"
                  className="flex-1 h-11 px-4 rounded-full bg-secondary border border-transparent focus:border-primary outline-none text-sm"
                />
                <button type="submit" disabled={busy || !input.trim()} data-testid="public-chat-send"
                  className="w-11 h-11 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 transition-transform active:scale-95">
                  <Send size={18} />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Launcher button */}
      {!open && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={openChat}
          data-testid="public-chat-launcher"
          className="w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-[0_12px_32px_rgba(75,35,204,0.4)] flex items-center justify-center"
          aria-label="Open chat"
        >
          <MessageCircle size={24} />
        </motion.button>
      )}
    </div>
  );
}
